import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from '@mui/material'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import './ContractBilling.scss'

// Import the SynerallAddress type
import type { SynerallAddress } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import useUser from '../../../contexts/hooks/useUser'
import ContactInfoAccordion from '../components/ContactInfoAccordion'
import { useSyneContractInfo } from '../hooks/trpcHooks'
import SaveAccountNumber from './SaveAccountNumber'

const AddressField = ({
	label,
	value,
	isMatchingAddress,
}: {
	label: string
	value?: string
	isMatchingAddress: boolean
}) => (
	<div className='address-field'>
		<label className={isMatchingAddress ? 'text-text-dark' : 'text-text-dark dark:text-white'}>
			{label}
		</label>
		<span
			className={
				isMatchingAddress ? 'text-text-grey-300' : 'text-text-grey-200 dark:text-text-grey-100'
			}
		>
			{value || '-'}
		</span>
	</div>
)

const ContractBilling = () => {
	const object = useContext(ObjectContext)

	const { t } = useTranslation('pages_contractInformation')

	const { user } = useUser()

	const { syneUser, customerAddress, isLoading, updateLanguage, hasActiveSupplyContract } =
		useSyneContractInfo(user?.personalId, object.synerallId, object.synerallContractId)

	const supportedLanguages = [
		{ code: 'fi', label: t('finnish') },
		{ code: 'en', label: t('english') },
	]

	const getFullAddressString = (address: SynerallAddress): string => {
		return [
			address.Street || '',
			address.House || '',
			address.Stairwell || '',
			address.Apartment || '',
			', ',
			address.ZipCode || '',
			' ',
			address.City || '',
			', ',
		]
			.filter(part => part !== '')
			.join(' ')
			.replace(/\s,\s/g, ', ')
			.trim()
	}

	return (
		<div id='contract-billing'>
			<ContactInfoAccordion />

			<Accordion className='mb-4'>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls='billing-content'
					id='billing-header'
				>
					<div className='title'>{t('billing.addressInfo')}</div>
				</AccordionSummary>
				<AccordionDetails>
					<div className='customer-service-notice text-text-dark dark:text-white bg-[#f5f5f5] dark:bg-[#0d2d30] border-l-4 border-l-[#] dark:border-l-[#031f22]'>
						{t('billing.contactCustomerService')}
					</div>
					{customerAddress && customerAddress.length > 0 && (
						<div className='address-info'>
							{customerAddress
								.sort((a, b) => {
									const aIsMatching = a.StreetAddress === object.streetAddress
									const bIsMatching = b.StreetAddress === object.streetAddress
									return bIsMatching ? 1 : aIsMatching ? -1 : 0
								})
								.map((address, index) => {
									const isMatchingAddress = address.StreetAddress !== object.streetAddress

									return (
										<div
											key={index}
											className={`address-entry ${isMatchingAddress ? 'border-l-4 bg-[#cae3cc] border-l-[#52ae32] dark:bg-[#acd7d4] dark:border-l-[#3cb49f]' : 'bg-[#f5f5f5] dark:bg-[#0d2d30]'}`}
										>
											<h4
												className={`border-b border-b-text-grey-200 text-[#127d27] ${isMatchingAddress ? 'dark:text-[#063a3e]' : 'dark:text-[#acd7d4]'}`}
											>
												{getFullAddressString(address)}
											</h4>

											<AddressField
												label={t('billing.customerId')}
												value={syneUser?.Id.toString() || '-'}
												isMatchingAddress={isMatchingAddress}
											/>

											<AddressField
												label={t('billing.contractId')}
												value={object?.synerallContractId?.toString() || '-'}
												isMatchingAddress={isMatchingAddress}
											/>

											<AddressField
												label={t('billing.gsrn')}
												value={object?.gsrn || '-'}
												isMatchingAddress={isMatchingAddress}
											/>

											<AddressField
												label={t('billing.streetAddress')}
												value={address.StreetAddress || '-'}
												isMatchingAddress={isMatchingAddress}
											/>

											<AddressField
												label={t('billing.city')}
												value={address.City || '-'}
												isMatchingAddress={isMatchingAddress}
											/>

											<AddressField
												label={t('billing.zipCode')}
												value={address.ZipCode || '-'}
												isMatchingAddress={isMatchingAddress}
											/>
										</div>
									)
								})}
						</div>
					)}
				</AccordionDetails>
			</Accordion>
			<Accordion className='mb-4'>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls='language-content'
					id='language-header'
				>
					<div className='title'>{t('billing.contactLanguage')}</div>
				</AccordionSummary>
				<AccordionDetails>
					<div className='language-selector'>
						<FormControl fullWidth>
							<InputLabel>{t('billing.languageLabel')}</InputLabel>
							<Select
								MenuProps={{
									disablePortal: true,
									disableScrollLock: true,
									slotProps: {
										paper: {
											sx: {
												zIndex: 9999,
												borderRadius: '16px',
											},
										},
									},
								}}
								value={syneUser?.Language || 'fi'} // Default to Finnish if no language is set
								onChange={e => {
									const selectedLanguage = e.target.value as string
									updateLanguage(selectedLanguage)
								}}
								label={t('billing.languageLabel')}
								disabled={isLoading}
								sx={{ borderRadius: '16px' }}
							>
								{supportedLanguages.map(language => (
									<MenuItem key={language.code} value={language.code}>
										{language.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
				</AccordionDetails>
			</Accordion>
			{hasActiveSupplyContract && <SaveAccountNumber />}
		</div>
	)
}

export default ContractBilling
